import { Table, TableContainer, Thead, Tr, Th, Tbody, Td, Box, SkeletonText, Button } from "@chakra-ui/react"
import { format } from "date-fns";

import DeleteIcon from "@/assets/DeleteIcon";
import EditIcon from "@/assets/EditIcon";
import { formatCurrency } from "@/utils/currency";
import DraftIcon from "@/assets/DraftIcon";
import ApprovedIcon from "@/assets/ApprovedIcon";
import RejectIcon from "@/assets/RejectIcon";
import UnpaidIcon from "@/assets/UnpaidIcon";
import PaidIcon from "@/assets/PaidIcon";
import Pagination from "../Layouts/Pagination";
import ButtonConfirm from "@/assets/ButtonConfirm";
import ButtonConfirmDisabled from "@/assets/ButtonConfirmDisabled";
import { COLORS, CURRENCY, INVOICE_STATUS, MONTHS_OPTION, PAYMENT_STATUS, ROLES, UPLOAD_TYPE } from "@/common/constants";
import CompletedIcon from "@/assets/CompletedIcon";
import PendingIcon from "@/assets/PendingIcon";
import InProgress from "@/assets/InProgress";
import FinanceIcon from "@/assets/FinanceIcon";
import { SireReviewRequestorInvoice } from "@/services/InvoiceSireReviewRequestorService";
import { useNavigate } from "react-router-dom";
import FileIcon from "@/assets/FileIcon";
import ButtonReview from "@/assets/ButtonReview";
import PayIcon from "@/assets/PayIcon";
import PayDisableIcon from "@/assets/PayDisableIcon";

type TableInvoiceSireReviewRequestorProps = {
    role: string,
    dataList: SireReviewRequestorInvoice[],
    isLoading: boolean,
    handleEdit: Function,
    handleDelete: Function,
    currentPageSize: number,
    handleChangePageSize: (size: number) => void,
    handleChangePageNumber: (page: number) => void,
    handlePay: Function,
    currentPageNumber: number,
    totalPageNumber: number,
    hasNextPage: boolean,
    handleDownloadZip: (id: string, uploadType: number) => void,
    handleConfirmPayment: (id: string) => void,
};

const ConfirmationColumn = (role: string) => {
    switch(role){
        case ROLES.FINANCE:
            return <Th color={COLORS.TABLE_HEADER.FOREGROUND} backgroundColor={COLORS.TABLE_HEADER.BACKGROUND} textTransform="none" fontSize={14} textAlign='center'>Payment Confirmation</Th>
        case ROLES.SUPER_ADMIN:
        case ROLES.REQUESTOR:
            return <Th color={COLORS.TABLE_HEADER.FOREGROUND} backgroundColor={COLORS.TABLE_HEADER.BACKGROUND} textTransform="none" fontSize={14} textAlign='center'>Payment</Th>
        default:
            return null;
    }
}

const InvoiceStatus = (status: number) => {
    switch(status){
        case INVOICE_STATUS.DRAFT:
            return <DraftIcon />
        case INVOICE_STATUS.APPROVED:
            return <ApprovedIcon />
        case INVOICE_STATUS.IN_PROGRESS:
            return <InProgress />
        case INVOICE_STATUS.REJECTED:
            return <RejectIcon />
        case INVOICE_STATUS.FINANCE:
            return <FinanceIcon />
        default:
            return null;
    }
}

const PaymentStatus = (status: number, role: string) => {
    switch(status){
        case PAYMENT_STATUS.UNPAID:
            return <UnpaidIcon />
        case PAYMENT_STATUS.PAID:
            return ((role === ROLES.REQUESTOR) ? <InProgress /> : <PaidIcon />)
        case PAYMENT_STATUS.PENDING:
            return <PendingIcon />
        case PAYMENT_STATUS.REJECTED:
            return <RejectIcon />
        case PAYMENT_STATUS.COMPLETED:
            return ((role === ROLES.REQUESTOR) ? <PaidIcon /> : <CompletedIcon />)
        default:
            return null;
    }
}

const TableInvoiceSireReviewRequestor = ({
    role,
    dataList,
    isLoading,
    handleEdit,
    handleDelete,
    currentPageSize,
    handleChangePageSize,
    handleChangePageNumber,
    handlePay,
    currentPageNumber,
    totalPageNumber,
    hasNextPage,
    handleDownloadZip,
    handleConfirmPayment,
}: TableInvoiceSireReviewRequestorProps) => {
    const navigate = useNavigate();

    const handleDetails = (id: string) => {
        navigate('/manage-invoices/sire-requestor/reviews', { state: { id, isRequestor: true } })
    }

    const hasInvoiceStatusColumn = (role === ROLES.SUPER_ADMIN || role === ROLES.FINANCE);

    const NUM_COLUMNS = hasInvoiceStatusColumn ? 13 : 12;
    return (
        <TableContainer bgColor="#fff" borderRadius="13">
            <Box overflowX="auto">
                <Table variant='simple'>
                    <Thead>
                        <Tr>
                            <Th color={COLORS.TABLE_HEADER.FOREGROUND} backgroundColor={COLORS.TABLE_HEADER.BACKGROUND} textTransform="none" fontSize={14}>Invoice ID</Th>
                            <Th color={COLORS.TABLE_HEADER.FOREGROUND} backgroundColor={COLORS.TABLE_HEADER.BACKGROUND} textTransform="none" fontSize={14}>Requestor Company</Th>
                            <Th color={COLORS.TABLE_HEADER.FOREGROUND} backgroundColor={COLORS.TABLE_HEADER.BACKGROUND} textTransform="none" fontSize={14}>Review Month</Th>
                            <Th color={COLORS.TABLE_HEADER.FOREGROUND} backgroundColor={COLORS.TABLE_HEADER.BACKGROUND} textTransform="none" fontSize={14}>Invoice Date</Th>
                            <Th color={COLORS.TABLE_HEADER.FOREGROUND} backgroundColor={COLORS.TABLE_HEADER.BACKGROUND} textTransform="none" fontSize={14}>Total Reviews</Th>
                            <Th color={COLORS.TABLE_HEADER.FOREGROUND} backgroundColor={COLORS.TABLE_HEADER.BACKGROUND} textTransform="none" fontSize={14}>Review Cost</Th>
                            <Th color={COLORS.TABLE_HEADER.FOREGROUND} backgroundColor={COLORS.TABLE_HEADER.BACKGROUND} textTransform="none" fontSize={14}>Total Amount</Th>
                            {/* <Th color={COLORS.TABLE_HEADER.FOREGROUND} backgroundColor={COLORS.TABLE_HEADER.BACKGROUND} textTransform="none" fontSize={14}>PPN (%)</Th> */}
                            <Th color={COLORS.TABLE_HEADER.FOREGROUND} backgroundColor={COLORS.TABLE_HEADER.BACKGROUND} textTransform="none" fontSize={14}>PPN (Amount)</Th>
                            <Th color={COLORS.TABLE_HEADER.FOREGROUND} backgroundColor={COLORS.TABLE_HEADER.BACKGROUND} textTransform="none" fontSize={14}>Invoice File</Th>
                            {hasInvoiceStatusColumn && <Th color={COLORS.TABLE_HEADER.FOREGROUND} backgroundColor={COLORS.TABLE_HEADER.BACKGROUND} textTransform="none" fontSize={14} textAlign='center'>Invoice Status</Th>}
                            <Th color={COLORS.TABLE_HEADER.FOREGROUND} backgroundColor={COLORS.TABLE_HEADER.BACKGROUND} textTransform="none" fontSize={14} textAlign='center'>Payment Status</Th>
                            {ConfirmationColumn(role)}
                            <Th color={COLORS.TABLE_HEADER.FOREGROUND} backgroundColor={COLORS.TABLE_HEADER.BACKGROUND} textTransform="none" fontSize={14}>Note</Th>
                            <Th color={COLORS.TABLE_HEADER.FOREGROUND} backgroundColor={COLORS.TABLE_HEADER.BACKGROUND} textTransform="none" fontSize={14} textAlign='center'>Action</Th>
                        </Tr>
                    </Thead>
                    <Tbody>
                        {isLoading &&
                            <Tr>
                                <Td colSpan={NUM_COLUMNS}>
                                    <SkeletonText mt='4' noOfLines={4} spacing='4' skeletonHeight='3' />
                                </Td>
                            </Tr>
                        }
                        {(!isLoading && (!dataList || dataList.length === 0)) &&
                            <Tr>
                                <Td colSpan={NUM_COLUMNS} textAlign="center">Data not found</Td>
                            </Tr>
                        }
                        {!isLoading && dataList?.map((item) => {
                            return (
                                <Tr key={item.id}>
                                    <Td>{item.invoiceNumber}</Td>
                                    <Td>{item.requestorName}</Td>
                                    <Td>{item.isMonthly && item.reviewMonth && item.reviewYear ? MONTHS_OPTION[item.reviewMonth] + ' ' + item.reviewYear : 'Single Review'}</Td>
                                    <Td>{item.invoiceDate ? format(item.invoiceDate, 'dd-MM-yyyy') : '-'}</Td>
                                    <Td><Button bgColor="#079455" borderRadius='full' color='white' height='25px' onClick={() => handleDetails(item.id)}>{item.totalReview} - Detail</Button></Td>
                                    <Td textAlign='right'>{item.costReview ? formatCurrency(item.costReview, CURRENCY.USD.code, 'en-US') : '-'}</Td>
                                    <Td textAlign='right'>{item.totalAmount ? formatCurrency(item.totalAmount, CURRENCY.IDR.code, 'id-ID') : '-'}</Td>
                                    {/* <Td textAlign='right'>{item.ppn ? item.ppn : '-'}</Td> */}
                                    <Td textAlign='right'>{item.ppnPrice ? formatCurrency(item.ppnPrice, CURRENCY.IDR.code, 'id-ID') : '-'}</Td>
                                    <Td>
                                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                                            {item.invoiceDownloadId ? <FileIcon onClick={() => handleDownloadZip(item.id, UPLOAD_TYPE.RequestorInvoice)} style={{ cursor: 'pointer' }} /> : <p>-</p>}
                                        </div>
                                    </Td>
                                    {hasInvoiceStatusColumn && <Td textAlign='center'>
                                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                                            {InvoiceStatus(item.invoiceStatus)}
                                        </div>
                                    </Td>}
                                    <Td textAlign='center'>
                                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                                            {PaymentStatus(item.paymentStatus, role)}
                                        </div>
                                    </Td>
                                    {(role === ROLES.FINANCE) && <Td>
                                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                                            {item.paymentStatus === PAYMENT_STATUS.PAID ?
                                                <ButtonConfirm onClick={() => handleConfirmPayment(item.id)} style={{ cursor: 'pointer' }} />
                                            :
                                                <ButtonConfirmDisabled />
                                            }
                                        </div>
                                    </Td>}
                                    {(role === ROLES.REQUESTOR || role === ROLES.SUPER_ADMIN) && <Td>
                                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                                            {(item.paymentStatus === PAYMENT_STATUS.UNPAID || item.paymentStatus === PAYMENT_STATUS.REJECTED) && item.invoiceStatus === INVOICE_STATUS.APPROVED ?
                                                <PayIcon onClick={() => handlePay(item.id)} style={{ cursor: 'pointer' }} />
                                            :
                                                <PayDisableIcon />
                                            }
                                        </div>
                                    </Td>}
                                    <Td>{item.notes || '-'}</Td>
                                    <Td style={{ display: 'flex', cursor: 'pointer', gap: 8, paddingTop: '26px', paddingBottom: '26px', justifyContent: 'center' }}>
                                        {(role === ROLES.SUPER_ADMIN && item.invoiceStatus === INVOICE_STATUS.DRAFT) ? <>
                                            <EditIcon onClick={() => handleEdit(item.id)} />
                                            <DeleteIcon onClick={() => handleDelete(item.id)} />
                                        </> : null}
                                        {([ROLES.FINANCE, ROLES.SUPER_ADMIN].includes(role) && item.invoiceStatus === INVOICE_STATUS.FINANCE) ? <>
                                            <ButtonReview onClick={() => handleEdit(item.id)} />
                                        </> : null}
                                    </Td>
                                </Tr>
                            )
                        })}
                    </Tbody>
                </Table>
            </Box>
            <Pagination currentPageSize={currentPageSize} currentPageNumber={currentPageNumber} totalPageNumber={totalPageNumber} handleChangePageSize={handleChangePageSize} handleChangePageNumber={handleChangePageNumber} hasNextPage={hasNextPage} />
        </TableContainer>
    )
}

export default TableInvoiceSireReviewRequestor;